import Breadcrumb from "components/shared/breadcrumb";
import { useEffect, useState, memo, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { calculateDiscountPercentage, getProductImage, toMoneyFormat, unixToShamsi } from "utils";
import { deliveryMethods } from "constants/delivery-methods";
import { paymentStatuses } from "constants/statuses";
import { productRoute, smallCropSize } from "constants/config";
import UseFetch from "hooks/useFetch";
import Actions from "./actions/actions";
import useAutoRequest from "hooks/useAutoRequest";
import { CSSTransition } from "react-transition-group";
import AutoRequestBtn from "../auto-request-btn";

const Edit = props => {
    const {
        pageData,
        id,
        setServerBreadcrumbDataCallback,
    } = props;

    const location = useLocation();
    const [fetchUrl, setFetchUrl] = useState(null);
    const { isLoading, data, error, mutate } = UseFetch(fetchUrl);
    const urlParams = new URLSearchParams(location.search);

    const [autoRequestStatus, setAutoRequestStatus] = useState(false);
    useAutoRequest(autoRequestStatus, fetchUrl, () => mutate(false));

    const getPreviousRoute = () => {
        const splitedRoute = location.pathname.split("/");
        splitedRoute.pop();

        let previousRoute = splitedRoute.join("/");
        const validParams = [];

        if (pageData && pageData.add_valid_params)
            pageData.add_valid_params.forEach(param => {
                const paramValue = urlParams.get(param);
                if (paramValue)
                    validParams.push(`${param}=${paramValue}`);
            })

        if (validParams.length)
            previousRoute += `?${validParams.join("&")}`;

        return previousRoute;
    }

    useEffect(() => {
        if (pageData)
            setFetchUrl(pageData.get_url.replace(":id", id));
        // eslint-disable-next-line
    }, [pageData])

    useEffect(() => {
        if (data && data.breadcrumbs?.length)
            setServerBreadcrumbDataCallback(data.breadcrumbs);
        else
            setServerBreadcrumbDataCallback([]);
        // eslint-disable-next-line
    }, [data])

    const serviceData = useMemo(() => {
        if (!isLoading && !error && data)
            return data.data;

        return {};
    }, [data, isLoading, error])

    const serviceStatus = useMemo(() => {
        if (!isLoading && !error) {
            const val = paymentStatuses.find(el => el.id === data.data.order.status_id);
            return val.title;
        }

        return "";
    }, [data, isLoading, error])

    const orderWeight = useMemo(() => {
        if (serviceData.order?.factor?.layers?.length) {
            let newWeight = 0;

            serviceData.order.factor.layers.forEach(layer => {
                if (layer.delivery_calculation?.weight)
                    newWeight += layer.delivery_calculation.weight;
            })

            return newWeight;
        }

        return 0;
    }, [serviceData])

    return (
        <>
            <div className="flex item-start md:items-center justify-start md:justify-between flex-col md:flex-row mb-10">
                <div className="mb-6 md:mb-0">
                    <h1 className="title-c1 tp-21">جزئیات</h1>
                    <Breadcrumb data={[
                        { title: "مدیریت فروشگاه" },
                        { title: "سفارشات", link: "/panel/order" },
                        { title: "جزئیات فاکتور" }
                    ]} />
                </div>

                <div className="flex">
                    <Link to={getPreviousRoute()} className="w-10 h-10 tp-22 text-[#424e79] dark:text-white rounded-md fit-center">
                        <i className="fa-regular fa-arrow-right"></i>
                    </Link>

                    <Actions data={serviceData} mutate={mutate} />

                    {
                        <CSSTransition in={!autoRequestStatus} timeout={200} unmountOnExit={true} appear={true}>
                            <button className="anim-c4 w-10 h-10 tp-22 text-[#424e79] dark:text-white mr-2 rounded-md fit-center" onClick={mutate} title="بروز رسانی">
                                <i className="fa-regular fa-arrows-retweet"></i>
                            </button>
                        </CSSTransition>
                    }

                    <div className="mr-2">
                        <AutoRequestBtn
                            active={autoRequestStatus}
                            clickHandler={() => setAutoRequestStatus(prev => !prev)}
                        />
                    </div>
                </div>
            </div>

            <div className="wrap-box tp-22">
                <div className="bg-yellow-100 flex items-center mb-5 rounded-md p-3 dark:bg-slate-800">
                    <i className="fa-regular fa-circle-exclamation text-sm text-green-600 dark:text-green-300 ml-3"></i>

                    <p className="flex text-sm text-green-800 dark:text-slate-300">
                        <b className="inline-block ml-2">وضعیت سفارش</b>
                        <span>{serviceStatus}</span>

                        {
                            serviceData.accept_change_by_customer === false && (
                                <span className="mr-4 text-red-600">سفارش در انتظار تایید خریدار است !</span>
                            )
                        }

                        {
                            serviceData.status_id === 1 && (
                                <span className="mr-4 text-yellow-700">سفارش پرداخت نشده است، امکان تایید سفارش پس از پرداخت مشتری امکان پذیر می باشد.</span>
                            )
                        }
                    </p>
                </div>

                {
                    !isLoading ? (
                        !error ? (
                            <>
                                <div className="border-b border-gray-200 dark:border-slate-700 py-4">
                                    <ul className="order-detail">
                                        <li className="inline-flex items-center">
                                            <span>شناسه گروه سفارش</span>
                                            <b>{serviceData.id}</b>
                                        </li>

                                        <li className="inline-flex items-center">
                                            <span>مبلغ فاکتور</span>
                                            <b>{toMoneyFormat(serviceData.order?.factor?.price)}<span className="mr-1"> تومان</span></b>
                                        </li>
                                    </ul>
                                </div>

                                <div className="border-b border-gray-200 dark:border-slate-700 py-4">
                                    <ul className="order-detail">
                                        <li className="inline">
                                            <span>تاریخ ثبت سفارش</span>
                                            <b>{serviceData.created_at ? unixToShamsi(serviceData.created_at) : "--"}</b>
                                        </li>

                                        <li className="inline">
                                            <span>تاریخ تایید</span>
                                            <b>{serviceData.order.confirmed_at ? unixToShamsi(serviceData.order.confirmed_at) : "---"}</b>
                                        </li>

                                        <li className="inline">
                                            <span>تاریخ تحویل</span>
                                            <b>{serviceData.order.delivered_at ? unixToShamsi(serviceData.order.delivered_at) : "---"}</b>
                                        </li>
                                    </ul>
                                </div>

                                <div className="border-b border-gray-200 dark:border-slate-700 py-4">
                                    <ul className="order-detail">
                                        <li className="inline-flex items-center">
                                            <span>نوع ارسال</span>
                                            <b>{serviceData.delivery_type_id ? deliveryMethods[serviceData.delivery_type_id] : "نامشخص"}</b>
                                        </li>

                                        {
                                            serviceData?.order?.address?.address?.length > 0 && (
                                                <li className="inline-flex items-center">
                                                    <span>آدرس مشتری</span>
                                                    <a
                                                        className="dark:text-white"
                                                        href={`https://www.google.com/maps/search/?api=1&query=${serviceData?.order?.address?.coordinates[1]},${serviceData?.order?.address?.coordinates[0]}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        <i className="fa-regular fa-location-dot ml-1"></i>
                                                        {serviceData?.order?.address?.address}
                                                    </a>
                                                </li>
                                            )
                                        }

                                        <li className="inline-flex items-center">
                                            <span>وزن محسابه شده توسط لایه های فاکتور</span>
                                            <b>{orderWeight} گرم</b>
                                        </li>
                                    </ul>
                                </div>

                                {
                                    serviceData?.description && serviceData?.description?.length ? (
                                        <div className="border-b border-gray-200 dark:border-slate-700 py-4">
                                            <ul className="order-detail">
                                                <li className="inline">
                                                    <span>توضحیات مشتری</span>
                                                    <b>{serviceData?.description}</b>
                                                </li>
                                            </ul>
                                        </div>
                                    ) : <></>
                                }

                                <div className="border-b border-gray-200 dark:border-slate-700 py-4">
                                    {
                                        serviceData?.items?.map(el => (
                                            <div className="flex items-center text-sm text-gray-900 dark:text-slate-300" key={`product-${el.product_id}`}>
                                                <a href={`${productRoute}/${el.product_id}`} className="tiny-product" target="_blank" rel="noreferrer">
                                                    <img src={getProductImage(el.meta_fields.e18_images, smallCropSize)} alt={el.title} />
                                                </a>

                                                <div className="flex grow">
                                                    <div className="grow"><a href={`${productRoute}/${el.product_id}`} target="_blank" rel="noreferrer" >{el.title}</a></div>
                                                    <div className="grow text-center">قیمت: <b className="font-medium">{toMoneyFormat(el.price)}</b></div>
                                                    <div className="grow text-center">تعداد: <b className="font-medium">{el.quantity}</b></div>
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>

                                <div className="border-b border-gray-200 dark:border-slate-700 py-4">
                                    <Link
                                        to={`/panel/transactions?order_group_id=${serviceData.id}`}
                                        className="text-sm bg-green-500 hover:bg-green-600 rounded-lg text-white px-2 py-1 dark:bg-slate-500 dark:hover:bg-slate-600 transition-colors"
                                    >
                                        نمایش تراکنش های سفارش
                                    </Link>
                                </div>

                                <div className="flex factor-sum items-stretch rounded-md overflow-hidden mt-4">
                                    <div className="bg-primary-100 text-white px-3 fit-center">
                                        <i className="fa-regular fa-square-list"></i>
                                    </div>

                                    <div className="grow">
                                        <ul>
                                            <li className="has-bb">
                                                <span>قیمت اصلی کالاها</span>
                                                <b>
                                                    {toMoneyFormat(serviceData?.order?.factor?.total)}{" "}
                                                    <span className="unit">تومان</span>
                                                </b>
                                            </li>

                                            {
                                                serviceData?.order?.factor?.profit > 0 && (
                                                    <li>
                                                        <span>تخفیف کالاها</span>
                                                        <b>
                                                            <span className="text-sm font-normal text-gray-500">
                                                                ({calculateDiscountPercentage(serviceData?.order?.factor?.total, serviceData?.order?.factor?.total - serviceData?.order?.factor?.profit)}%)
                                                            </span> {" "}

                                                            {toMoneyFormat(serviceData?.order?.factor?.profit)} {" "}
                                                            <span className="unit"> تومان</span>
                                                        </b>
                                                    </li>
                                                )
                                            }

                                            <li className="has-bb">
                                                <span>قیمت پس از تخفیف</span>
                                                <b>
                                                    {toMoneyFormat(serviceData?.order?.factor?.total - serviceData?.order?.factor?.profit)} {" "}
                                                    <span className="unit"> تومان</span>
                                                </b>
                                            </li>

                                            {
                                                serviceData?.order?.factor?.layers.length > 0 && (
                                                    serviceData?.order?.factor?.layers.map(layer => (
                                                        <li key={`layer-section-${layer.id}`}>
                                                            <span>{layer.title}</span>
                                                            <b>
                                                                {toMoneyFormat(layer.amount)}{" "}
                                                                <span className="unit">تومان</span>
                                                            </b>
                                                        </li>
                                                    ))
                                                )
                                            }

                                            <li className={serviceData?.order?.factor?.layers.length ? "has-bt" : ""}>
                                                <span>جمع سبد خرید</span>
                                                <b>
                                                    {toMoneyFormat(serviceData?.order?.factor?.price)}{" "}
                                                    <span className="unit">تومان</span>
                                                </b>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </>
                        ) : <p className="font-medium text-sm text-red-500 text-center">خطا در دریافت اطلاعات!</p>
                    ) : <></>
                }
            </div >
        </>
    )
}

export default memo(Edit);